import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as actionTypes from 'store/actionTypes';

import { useQuery } from 'shared/hooks/useQuery';
import CustomButton from 'shared/components/customButton/customButton';
import { BackIcon, EditIcon, GermanyFlag, MenuIcon, TickIcon, UKFlag, WalletIcon } from 'shared/components/icons/icons';
import { API_CONFIG } from 'shared/constants/api';
import AuthService from 'shared/services/auth.service';
import HttpService from 'shared/services/http.service';
import { refreshLanguage } from 'shared/util/localization';
import localizationConstants from 'shared/util/translation/constant';
import { createAction, handleAuthTrack, isWhiteLabel, prizeFormatter } from 'shared/util/utility';
import Modal from 'shared/components/modal/modal';
import { Translate } from 'shared/components/translate/translate';
import { IState } from 'shared/interface/state';
import { lobbyFilterTags } from 'features/lobby/constants/lobby';

import InviteFriend from '../components/inviteFriend';
import HowToPlay from '../components/howToPlay';
import { LOBBY_REDIRECTION_TABS, PROFILE_DETAILS } from '../constants/header';
import { IProfileDetails } from '../interface/header';

import WhiteLogo from 'assets/images/logo-white.png';
import GreyLogo from 'assets/images/logo.png';
import StippzMiniLogo from 'assets/images/mini-logo.png';
import MaskGroup from 'assets/images/mask-group.png';
import WhiteLabelLogo from 'assets/images/white_label_logo.png';

const Header: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	const query: URLSearchParams = useQuery();
	const contestType: string = query.get('contestType') || '';
	const redirectionTab: string = LOBBY_REDIRECTION_TABS[contestType as string] || '';
	const isLogin: boolean = useSelector((state: IState) => state.auth.isLogin);

	const language = (query.get('language') ? query.get('language') : localStorage.getItem('lang')) || 'de';
	const [isSidebarOpen, setIsSideBarOpen] = useState<boolean>(false);
	const [isInviteFriend, setIsInviteFriend] = useState<boolean>(false);
	const [isActiveLanguage, setIsActiveLanguage] = useState({
		germany: language === 'de',
		english: language === 'en'
	});
	const [profileDetails, setProfileDetails] = useState<IProfileDetails>(PROFILE_DETAILS);
	const [isHowToPlay, setIsHowToPlay] = useState<boolean>(false);
	const [showVersionUpdatePopup, setShowVersionUpdatePopup] = useState(false);
	const [xVersion, setXVersion] = useState(localStorage.getItem('x-version'));

	const handleLogout = () => {
		handleAuthTrack();
		AuthService.removeAuthData();
		dispatch(createAction(actionTypes.LOGOUT));
		navigate('/login');
	};

	const getProfileDetails = useCallback(
		(upgradeVersion = false) => {
			let otherParams = {};
			if (!isWhiteLabel) {
				otherParams = {
					isResponseHeader: true,
					upgradedVersion: true
				};

				if (upgradeVersion) {
					localStorage.setItem('x-version', xVersion!);
				}
			}

			HttpService.get(API_CONFIG.path.me, {}, otherParams)
				.then((res) => {
					//x-version functionality is only for STIPPZ
					if (!isWhiteLabel) {
						const version = res?.headers['x-version'];
						const currentVersion = localStorage.getItem('x-version');

						setXVersion(version);
						if (currentVersion === null || version !== currentVersion) {
							setShowVersionUpdatePopup(true);
						} else {
							setProfileDetails(res?.data?.data || PROFILE_DETAILS);
							dispatch(createAction(actionTypes.USER_PROFILE_DATA, res?.data?.data));
						}

						if (upgradeVersion) {
							localStorage.setItem('x-version', version);
							window.location.reload();
						}
					} else {
						setProfileDetails(res || PROFILE_DETAILS);
						dispatch(createAction(actionTypes.USER_PROFILE_DATA, res));
					}
				})
				.catch((error) => {
					console.error(error);
				});
		},
		[dispatch]
	);

	useEffect(() => {
		isLogin && getProfileDetails();
	}, [pathname, getProfileDetails, isLogin]);

	const { avatarUrl, userName, email, totalPlays, totalWins, walletAmount, wonAmount } = profileDetails;

	const TournamentDetails = [
		{ name: 'Contests', amount: totalPlays ?? '-' },
		{ name: t(localizationConstants.sideBarWins), amount: totalWins ?? '-' },
		{
			name: t(localizationConstants.winRate),
			amount: totalWins && totalPlays ? `${Math.round((totalWins * 100) / totalPlays)}%` : '0%'
		},
		// Profit item only show if isWhiteLabel is false
		...(!isWhiteLabel ? [{ name: t(localizationConstants.profit), amount: prizeFormatter(wonAmount) }] : [])
	];

	const sideMenuRef: React.RefObject<HTMLInputElement> = useRef(null);

	useEffect(() => {
		const handleOutsideClick = (event: Event) => {
			if (sideMenuRef.current && !sideMenuRef.current.contains(event.target as HTMLInputElement)) {
				// Clicked outside the sidebar, so close it
				setIsSideBarOpen(false);
			}
		};

		// Attach the event listener when the component mounts
		document.addEventListener('mousedown', handleOutsideClick);

		// Detach the event listener when the component unmounts
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, []);

	useEffect(() => {
		if (isSidebarOpen) {
			document.body.style.overflow = 'hidden';
		}

		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [isSidebarOpen]);

	const handleBackIconClick = () => {
		switch (true) {
			case pathname === '/wallet/top-up':
				navigate(-1);
				break;
			case !isLogin && pathname.includes('/contest-details'):
				navigate('/arena');
				break;
			case isLogin && pathname.includes('/contest-details') && contestType !== lobbyFilterTags.Arena:
				navigate('/', {
					state: { activeTab: redirectionTab }
				});
				break;
			default:
				navigate('/');
		}
	};

	return (
		<>
			<header className='header-wrapper bg--white position--fixed'>
				<nav
					className={`navbar flex align-items--center justify-content--between height--full width--full ${
						isWhiteLabel ? 'bg--golden-yellow' : ''
					}`}
				>
					<div className={`menu-wrapper flex align-items--center ${isLogin ? 'width--30' : ''}`}>
						{pathname === '/' ? (
							isLogin && (
								<div
									className={`${
										isSidebarOpen ? '' : 'z-index--1'
									} menu-icon-wrapper cursor--pointer ${
										isWhiteLabel ? 'bg--golden-yellow' : 'bg--white'
									}`}
									onClick={() => setIsSideBarOpen(true)}
								>
									<MenuIcon />
								</div>
							)
						) : pathname !== '/arena' ? (
							<div
								className={`z-index--1 menu-icon-wrapper cursor--pointer ${
									isWhiteLabel ? 'bg--golden-yellow' : 'bg--white'
								}`}
								onClick={() => handleBackIconClick()}
							>
								<BackIcon />
							</div>
						) : (
							<></>
						)}

						{isLogin && (
							<div className='profile-details ml--20'>
								<div className='image-wrapper border--gradient-red border-radius--half'>
									<img
										src={avatarUrl || MaskGroup}
										alt='profile image'
										className='width--full height--full object-fit--cover'
									/>
								</div>
								<p className='font--medium font-size--xxs text--primary ml--7'>{userName}</p>
							</div>
						)}
					</div>
					<div className={isLogin ? '' : 'width--full flex justify-content--center'}>
						<div
							className={`${isWhiteLabel ? 'white-label-logo-wrapper' : 'logo-wrapper'} cursor--pointer`}
							onClick={() => navigate('/')}
						>
							<img
								src={isWhiteLabel ? WhiteLabelLogo : GreyLogo}
								alt='Stippz Logo'
								className='width--full height--full'
							/>
						</div>
					</div>
					<div className='flex justify-content--end width--30'>
						{isLogin && (
							<div className='wallet-wrapper flex align-items--center justify-content--end mr--10'>
								{!isWhiteLabel && (
									<p className='wallet-amount font--semi-bold font-size--browser-default text--primary'>
										{prizeFormatter(walletAmount) ?? '0 €'}
									</p>
								)}
								<div
									className={`wallet-icon-wrapper cursor--pointer ${
										isWhiteLabel ? 'bg--golden-yellow' : 'bg--gradient-red'
									}`}
									onClick={() => navigate('/wallet')}
								>
									<WalletIcon fill={isWhiteLabel ? '#344154' : ''} />
								</div>
							</div>
						)}
						{isWhiteLabel && (
							<div className='powered-by-wrapper flex--column'>
								<p className='powered-by-text color--primary text--center font--semi-bold mb--5'>
									POWERED BY
								</p>
								<div className='powered-by-logo'>
									<Link
										to='https://app.stippz.com/sign-up?language=de'
										target='_blank'
										className='show width--full height--full'
									>
										<img
											src={window.innerWidth > 480 ? GreyLogo : StippzMiniLogo}
											alt='powered-by-logo'
											className='width--full height--full'
										/>
									</Link>
								</div>
							</div>
						)}
					</div>
				</nav>
				<div className={`${isSidebarOpen ? 'expand' : 'collapsed'} sidebar-bg height--full position--fixed`}>
					<div
						className={`sidebar-menu-wrapper flex align-items--center width--full height--full ${
							isWhiteLabel ? 'white-label-sidebar-menu-bg' : 'sidebar-menu-bg'
						}`}
					>
						<div ref={sideMenuRef} className='profile-details-container width--full height--full'>
							<div className='cursor--pointer close-icon' onClick={() => setIsSideBarOpen(false)}>
								<BackIcon className='sidebar-back-icon' />
							</div>
							<div className='justify-align--center'>
								<div className='sidebar-logo-wrapper text--center mt--30 mb--20 '>
									<img
										src={isWhiteLabel ? WhiteLabelLogo : WhiteLogo}
										alt='Stippz Logo'
										className='width--full height--full'
									/>
								</div>
							</div>
							<div className='profile-image-wrapper border--gradient-red border-radius--half m--0-auto'>
								<img
									src={avatarUrl || MaskGroup}
									alt='profile image'
									className='width--full height--full object-fit--cover'
								/>
							</div>
							<div className='mt--25 flex justify-content--between'>
								<div className='text--white'>
									<p className='font--bold font-size--lg'>{userName}</p>
									<p
										className='font--medium font-size--xxs mt--5 truncate--text cursor--pointer email-wrapper'
										title={email}
									>
										{email}
									</p>
								</div>
								<div
									onClick={() => {
										navigate('/edit-profile');
										setIsSideBarOpen(false);
									}}
									className='edit-button cursor--pointer'
								>
									<EditIcon />
								</div>
							</div>
							<div className='tournament-winning-details text--white mt--15 flex flex--wrap text--center justify-content--center'>
								{TournamentDetails.map(({ name, amount }, index: number) => (
									<div key={index} className='winning-details'>
										<p className='font-size--10 text--uppercase winning-title'>{name}</p>
										<p className='font-size--lg font--bold'>{amount}</p>
									</div>
								))}
							</div>
							<div className='mt--10 text--center'>
								<CustomButton
									buttonTitle={t(localizationConstants.rewards)}
									btnClassName='primary-button rewards-btn'
									onButtonClick={() => {
										navigate('/wallet', { state: { rewards: true } });
										setIsSideBarOpen(false);
									}}
								/>
								<CustomButton
									buttonTitle={t(localizationConstants.arena)}
									btnClassName='transparent-button mt--15'
									onButtonClick={() => {
										navigate('/', { state: { activeTab: t(localizationConstants.arena) } });
										setIsSideBarOpen(false);
									}}
								/>
								<CustomButton
									buttonTitle={t(localizationConstants.inviteFriends)}
									btnClassName='mt--15 transparent-button invite-friend-btn'
									onButtonClick={() => {
										setIsInviteFriend(true);
										setIsSideBarOpen(false);
									}}
								/>
								{!isWhiteLabel ? (
									<CustomButton
										buttonTitle={t(localizationConstants.createContest)}
										btnClassName='mt--15 primary-button create-contest-btn'
										onButtonClick={() => {
											navigate('/create-contest');
											setIsSideBarOpen(false);
										}}
									/>
								) : (
									<CustomButton
										buttonTitle={t(localizationConstants.howToPlay)}
										btnClassName='mt--15 transparent-button invite-friend-btn'
										onButtonClick={() => {
											setIsHowToPlay(true);
											setIsSideBarOpen(false);
										}}
									/>
								)}
								<div className='mt--15'>
									<button
										className='bg--transparent no--padding position--relative mr--20'
										onClick={() => {
											setIsActiveLanguage({
												germany: true,
												english: false
											});
											refreshLanguage('de');
										}}
									>
										<GermanyFlag />
										{isActiveLanguage.germany && <TickIcon className='tick-icon' />}
									</button>

									<button
										className='bg--transparent no--padding position--relative'
										onClick={() => {
											setIsActiveLanguage({
												germany: false,
												english: true
											});
											refreshLanguage('en');
										}}
									>
										<UKFlag />
										{isActiveLanguage.english && <TickIcon className='tick-icon' />}
									</button>
								</div>
								<CustomButton
									onButtonClick={handleLogout}
									buttonTitle={t(localizationConstants.logout)}
									btnClassName='transparent-button logout-button mt--15'
								/>
							</div>
						</div>
					</div>
					{isInviteFriend && <InviteFriend handleClose={() => setIsInviteFriend(false)} />}
					{isHowToPlay && <HowToPlay handleClose={() => setIsHowToPlay(false)} />}
				</div>
			</header>
			{showVersionUpdatePopup && (
				<Modal title='' isShowCloseIcon={false} closeModal={() => {}} isFooter={false} className=''>
					<p className='text--center mb--35 font--bold font-size--md line-height--md align-items--center'>
						<Translate text={localizationConstants.versionUpgrade} />
					</p>
					<p className='text--center mt--20 mb--5 font-size--sm align-items--center'>
						<Translate text={localizationConstants.updateAvailableMsg} />
					</p>
					<p className='text--center mb--35 font-size--sm align-items--center'>
						<Translate text={localizationConstants.refreshMsg} />
					</p>
					<div className='justify-align--center'>
						<CustomButton
							buttonTitle={t(localizationConstants.viewUpdates)}
							btnClassName='primary-button mb--20 '
							onButtonClick={() => {
								getProfileDetails(true);
							}}
						/>
					</div>
				</Modal>
			)}
		</>
	);
};

export default Header;
